import { ReactNode, useEffect, useMemo, useState } from 'react';
import {
    Card,
    Spin,
    Divider,
    Select,
    Input,
    Button,
    Form,
    message,
    Modal,
    Switch,
} from 'antd';
import { useNavigate, useParams } from 'react-router-dom';
import '../../styles/_createMedOrder.scss';
import {
    useGetStructuredActionsQuery,
    useGetDrugInteractionMutation,
    useGetStructuredFormsQuery,
    useGetStructuredFrequenciesQuery,
    useGetStructuredRouteQuery,
    useGetPatientDetailsQuery,
    useSearchDrugFormatListMutation,
    useSearchDrugListMutation,
} from '../../features/patient/patientService';
import useDebounce from '../../hooks/useDebounce';
import { MedOrderRequest, OrderResponse } from '../../models/PatientModels';
import useOrderToast from '../../hooks/useOrderToast';
import RoundedButton from '../RoundedButton';
import { WarningOutlined } from '@ant-design/icons';
import { AiOutlineLeft, AiOutlineRight } from 'react-icons/ai';
import ConfirmationModal from './ConfirmationModal';

type FieldType = {
    searchDrug?: string;
    drug?: string;
    drugFormat?: string;
    drugQuantity?: string;
    directions?: string;
    action?: string;
    quantity?: number;
    formValue?: string;
    route?: string;
    frequency?: { value: string; conceptId: string; label: string };
    directionSwitch?: boolean;
};

const OrderMedicationForm = ({
    order,
    cancelButton,
    onSubmit,
    isSubmitting,
    submitLabel,
    toastMessage,
    isStructuredAllowed = true,
    isOrderTemplateCreation = false,
}: {
    order?: OrderResponse | undefined;
    cancelButton: ReactNode;
    onSubmit: Function;
    submitLabel: string;
    isSubmitting: boolean;
    toastMessage: string;
    isStructuredAllowed?: boolean,
    isOrderTemplateCreation?: boolean,
}) => {
    const navigate = useNavigate();
    const { id: patientId = '' } = useParams();
    const { orderSetId: orderSetId = '' } = useParams();
    const [
        searchDrugDetails,
        { data: drugList, isLoading: isDrugListLoading },
    ] = useSearchDrugListMutation();
    const [
        searchDrugFormat,
        { isLoading: isDrugFormatListLoading, data: drugFormatList },
    ] = useSearchDrugFormatListMutation();
    const { data: structuredActions } = useGetStructuredActionsQuery();
    const { data: structuredForms } = useGetStructuredFormsQuery();
    const { data: structuredRoute } = useGetStructuredRouteQuery();
    const { data: structuredFrequency } = useGetStructuredFrequenciesQuery();
    const [form] = Form.useForm();
    const [search, setSearch] = useState<string>('');
    const [searchValue, setSearchValue] = useState<string>('');
    const [isFirstLoad, setIsFirstLoad] = useState<boolean>(false);
    const [seletedMedId, setSelectedMedId] = useState<string>('');
    const [directionSwitch, setDirectionSwitch] = useState<boolean>(isStructuredAllowed ? false : true);
    const [showwarning, setShowwarning] = useState<boolean>(false);
    const [isModalOpen, setIsModalOpen] = useState(false);

    const { toast } = useOrderToast();

    const handleSearch = (value: string) => {
        setSearch(value);
        setSearchValue(value);
    };

    const { data: patientDetails } = useGetPatientDetailsQuery({
        id: patientId,
    });

    const [
        searchdrugInteraction,
        { data: drugInteraction, isLoading: isLoadingDrugInteraction },
    ] = useGetDrugInteractionMutation();

    const directionActions = useMemo(() => {
        if (structuredActions && structuredActions.length > 0) {
            return structuredActions.map((action) => ({
                value: action.conceptId,
                label: action.name,
            }));
        }
        return [];
    }, [structuredActions]);

    const directionForms = useMemo(() => {
        if (structuredForms && structuredForms.length > 0) {
            return structuredForms.map((action) => ({
                value: action.potencyUnit,
                label: action.name,
            }));
        }
        return [];
    }, [structuredForms]);

    const directionRoute = useMemo(() => {
        if (structuredRoute && structuredRoute.length > 0) {
            return structuredRoute.map((action) => ({
                value: action.conceptId,
                label: action.name,
            }));
        }
        return [];
    }, [structuredRoute]);

    const directionFrequency = useMemo(() => {
        if (structuredFrequency && structuredFrequency.length > 0) {
            const maping = structuredFrequency.map((action) => ({
                value: `${action.conceptId}-${action.frequencyUnitsCode}`,
                conceptId: action.conceptId,
                frequencyUnitsCode: action.frequencyUnitsCode,
                label: action.name,
            }));
            return maping;
        }
        return [];
    }, [structuredFrequency]);

    const searchDebouced = useDebounce(search, 500);
    useEffect(() => {
        const fetchSearchResults = async (query: string) => {
            await searchDrugDetails({ value: query });
        };

        if (searchDebouced && searchDebouced.trim())
            fetchSearchResults(searchDebouced);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [searchDebouced]);

    useEffect(() => {
        if (!order) return;
        setIsFirstLoad(true);

        const fetchSearchDrugFormat = async (query: string) => {
            return await searchDrugFormat({ value: query });
        };

        fetchSearchDrugFormat(order.routedMedId.toString()).then((response) => {
            if (!('data' in response)) return;

            const selectedDrugFormat = response.data.find(
                (drugFormat: any) =>
                    order.routedMedId === drugFormat.routeD_MED_ID,
            );
            if (!selectedDrugFormat) return;
            const frequency = directionFrequency.find(
                (dr) =>
                    dr.frequencyUnitsCode ===
                        order?.structuredDirections?.frequencyUnitCode &&
                    dr.conceptId === order?.structuredDirections?.timingCode,
            );
            form.setFieldsValue({
                searchDrug: order.drugName,
                drug: {
                    value: order.drugName,
                    label: order.drugName,
                },
                drugFormat: {
                    value: selectedDrugFormat.id,
                    label: order.drugFormat,
                },
                drugQuantity: order.quantity,
                directions: order.directions,
                action:
                    directionActions.find(
                        (da) =>
                            da.value ===
                            order?.structuredDirections?.actionCode,
                    ) || undefined,
                formValue:
                    directionForms.find(
                        (df) =>
                            df.value === order?.structuredDirections?.formCode,
                    ) || undefined,
                quantity: order?.structuredDirections?.quantity,
                route:
                    directionRoute.find(
                        (dr) =>
                            dr.value === order?.structuredDirections?.routeCode,
                    ) || undefined,
                frequency: frequency,
            });
            setDirectionSwitch(!order.isDescriptionStructured);
            setIsFirstLoad(false);

            const gcN_SEQNO = response.data
                .map((item: any) => item.gcN_SEQNO)
                .join(', ');
            const routeD_MED_ID = response.data[0]?.routeD_MED_ID;

            searchdrugInteraction({
                req: {
                    prescribedGcnSeqno: gcN_SEQNO,
                    prescribedRoutedMedId: routeD_MED_ID,
                    patientId: patientDetails?.scriptSurePatientId ?? 0,
                    drugName: form.getFieldValue('drug')?.label,
                },
            });

            setShowwarning(true);
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [form, order]);

    const handleChange = (_: any, option: any) => {
        const { conceptId, frequencyUnitsCode, label } = option;
        form.setFieldsValue({
            frequency: { frequencyUnitsCode, conceptId, label },
        });
    };

    const handleSubmit = async () => {
        try {
            form.setFieldValue('directionSwitch', true);
            form.setFieldValue('drugFormat', form.getFieldValue('drugFormat'));
            const values = await form.validateFields();
            if (!drugFormatList) return;

            const selectedDrugFormat = drugFormatList.find(
                (drugFormat: any) => values.drugFormat.value === drugFormat.id,
            );
            if (!selectedDrugFormat) return;

            const payload: MedOrderRequest = {
                orderSetId: orderSetId,
                ROUTED_MED_ID: selectedDrugFormat.routeD_MED_ID,
                GCN_SEQNO: selectedDrugFormat.gcN_SEQNO,
                rxNorm: selectedDrugFormat.rxNorm,
                rxNormQualifier: selectedDrugFormat.rxNormQualifier,
                ndc: selectedDrugFormat.ndc,
                drugName: values.drug.label,
                quantity: values.drugQuantity,
                quantityQualifier: '',
                drugFormat: values.drugFormat.label,
                directions:
                    values.directions ??
                    `${values.action.label} ${values.quantity} ${values.formValue.label} ${values.route.label} ${values.frequency.label}`,
                orderSource: 0,
                isDescriptionStructured: !directionSwitch,
                structuredDirections: {
                    ActionCode: values.action?.value ?? '',
                    FormCode: values.formValue?.value ?? '',
                    Quantity: values.quantity ? Number(values.quantity) : 0,
                    FrequencyUnitCode:
                        values.frequency?.frequencyUnitsCode ?? '',
                    RouteCode: values.route?.value ?? '',
                    TimingCode: values.frequency?.conceptId,
                    TimingCodeText: values.frequency?.label,
                },
            };
            const response: any = await onSubmit({
                data: payload,
                patientId: patientId,
            });

            if (response?.data) {
                setSearchValue('');
                setIsModalOpen(true);
                //order || isOrderTemplateCreation ? navigate(-1) : form.resetFields();
            }
        } catch (error: any) {
            console.error('Validation Failed:', error);
            if (error?.errorFields?.length > 0) {
                message.error('Please fill all required fields!');
            } else {
                message.error(JSON.stringify(error));
            }
        }
    };

    const onSelectDrug = async (
        _: {
            value: string;
            label: string;
        },
        option: {
            value: string;
            label: string;
            medId: string;
            gcnSeqNos: string;
        },
    ) => {
        form.resetFields(['drugFormat']);

        const _drugFormatList = await searchDrugFormat({
            value: option.medId.toString(),
        });

        setSelectedMedId(option.medId.toString());

        if (typeof _drugFormatList === 'object' && !('data' in _drugFormatList))
            return;

        const gcN_SEQNO = _drugFormatList.data
            .map((item: any) => item.gcN_SEQNO)
            .join(', ');
        const routeD_MED_ID = _drugFormatList.data[0]?.routeD_MED_ID;

        searchdrugInteraction({
            req: {
                prescribedGcnSeqno: option.gcnSeqNos,
                prescribedRoutedMedId: routeD_MED_ID,
                patientId: patientDetails?.scriptSurePatientId ?? 0,
                drugName: form.getFieldValue('drug')?.label,
            },
        });

        setShowwarning(true);
    };

    const [open, setOpen] = useState<boolean>(false);

    const isSubmittingOrLoading =
        isSubmitting || (!!order && isFirstLoad) || isDrugListLoading;

    const isStructuredDescription = useMemo(
        () => !directionSwitch && !order,
        [directionSwitch, order],
    );

    const [loading, setLoading] = useState<boolean>(true);

    const showLoading = () => {
        setOpen(true);
        setLoading(true);

        // Simple loading mock. You should add cleanup logic in real world.
        setTimeout(() => {
            setLoading(false);
        }, 2000);
    };

    return (
        <>
            <Spin spinning={isSubmittingOrLoading} size="large">
                <Card
                    style={{ width: '100%' }}
                    bodyStyle={{ padding: 0 }}
                    className="shadow-sm mb-5 mt-4 bg-body"
                >
                    <div className="card-container-title mt-2">
                        Medication Information
                    </div>
                    <Divider className="my-3" />
                    <div className="mx-3">
                        <Form
                            form={form}
                            name="basic"
                            layout="vertical"
                            labelCol={{ span: 8 }}
                            wrapperCol={{ span: 16 }}
                            style={{ maxWidth: 680 }}
                            autoComplete="off"
                        >
                            <Form.Item<FieldType>
                                label={
                                    <div className="d-flex align-items-center gap-2">
                                        <label className="field-title">
                                        Search & Select Drug
                                        </label>
                                        {!isOrderTemplateCreation && drugInteraction?.interactions &&
                                            drugInteraction.interactions.length >
                                            0 &&
                                            showwarning && (
                                                <RoundedButton
                                                    onClick={showLoading}
                                                    className={
                                                        'fw-semibold bg-bright-orange text-white fw-semibold'
                                                    }
                                                    size="small"
                                                    fontSize={10}
                                                >
                                                    <WarningOutlined
                                                        rev={undefined}
                                                    />
                                                    WARNING : &nbsp;
                                                    {
                                                        drugInteraction
                                                            .interactions[0].total
                                                    }
                                                </RoundedButton>
                                            )}
                                    </div>
                                }
                                className="ms-4 bg-white"
                                name="drug"
                                rules={[
                                    {
                                        required: true,
                                        message: 'Please select drug',
                                    },
                                ]}
                            >
                                <Select
                                    placeholder="Select Drug"
                                    options={(drugList || []).map((item: any) => ({
                                        value: item.id,
                                        label: item.meD_ROUTED_MED_ID_DESC,
                                        medId: item.routeD_MED_ID,
                                        gcnSeqNos: item.gcN_SEQNO,
                                    }))}
                                    showSearch={true}
                                    labelInValue
                                    onSelect={onSelectDrug}
                                    allowClear
                                    onSearch={handleSearch}
                                    filterOption={false}
                                    className="drug-format-select rounded"
                                    getPopupContainer={(trigger) => trigger.parentNode}
                                />
                            </Form.Item>

                            <Form.Item<FieldType>
                                label={
                                    <label className="field-title">
                                        Select Drug Format
                                    </label>
                                }
                                className="ms-4"
                                name="drugFormat"
                                rules={[
                                    {
                                        required: true,
                                        message: 'Please select drug format!',
                                    },
                                ]}
                            >
                                <Select
                                    placeholder="Drug Format Based on Drug"
                                    options={(drugFormatList || [])?.map(
                                        (item: any) => ({
                                            value: item.id,
                                            label: `${item.meD_STRENGTH} ${item.meD_STRENGTH_UOM} ${item.meD_DOSAGE_FORM_DESC}`,
                                        }),
                                    )}
                                    optionLabelProp="label"
                                    loading={isDrugFormatListLoading}
                                    notFoundContent={
                                        isDrugFormatListLoading ? (
                                            <Spin size="small" />
                                        ) : null
                                    }
                                    labelInValue
                                    className="drug-format-select rounded"
                                    getPopupContainer={(trigger) => trigger.parentNode}
                                />
                            </Form.Item>
                            <Form.Item<FieldType>
                                label={
                                    <label className="field-title">
                                        Drug Quantity
                                    </label>
                                }
                                className="ms-4"
                                name="drugQuantity"
                                rules={[
                                    {
                                        required: true,
                                        message: 'Please enter drug quantity!',
                                    },
                                    {
                                        validator: (_, value) => {
                                            if (!value) {
                                                return Promise.reject();
                                            }

                                            const numericValue = parseFloat(value);
                                            if (isNaN(numericValue)) {
                                                return Promise.reject(
                                                    'Only numbers are allowed!',
                                                );
                                            }
                                            if (numericValue < 0.0001) {
                                                return Promise.reject(
                                                    'Min value is 0.0001',
                                                );
                                            }
                                            return Promise.resolve();
                                        },
                                    },
                                ]}
                            >
                                <Input
                                    placeholder="Input Drug Quantity"
                                    maxLength={10}
                                    className="field-bg"
                                    onChange={(e) => {
                                        const value = e.target.value.replace(
                                            /[^0-9.]/g,
                                            '',
                                        );
                                        form.setFieldsValue({
                                            drugQuantity: value,
                                        });
                                    }}
                                />
                            </Form.Item>

                            <div className="mx-4 d-flex flex-row">
                                <Switch
                                    checked={directionSwitch}
                                    disabled={!isStructuredAllowed}
                                    onChange={setDirectionSwitch}
                                    className="mx-2"
                                    style={{
                                        backgroundColor: directionSwitch
                                            ? '#0067B1'
                                            : '#7F808C',
                                    }}
                                />
                                <span>
                                Dosage Directions{' '}
                                    {!directionSwitch
                                        ? '(Structured SIG)'
                                        : '(Free Form)'}
                            </span>
                            </div>
                            <Divider className="mb-3" />
                            {directionSwitch ? (
                                <Form.Item<FieldType>
                                    label={
                                        <label className="field-title">
                                            Directions (Free Form)
                                        </label>
                                    }
                                    className="ms-4"
                                    name="directions"
                                    rules={[
                                        {
                                            required: directionSwitch,
                                            message:
                                                'Please select drug directions!',
                                        },
                                    ]}
                                >
                                    <Input.TextArea
                                        rows={6}
                                        className="field-bg"
                                        placeholder="Enter Directions"
                                        showCount
                                        maxLength={140}
                                    />
                                </Form.Item>
                            ) : (
                                <div
                                    className=" my-3 mb-5 d-flex flex-row justify-content-between"
                                    style={{ width: 1000 }}
                                >
                                    <Form.Item<FieldType>
                                        label={
                                            <label className="field-title">
                                                Action
                                            </label>
                                        }
                                        className="ms-4"
                                        name="action"
                                        rules={[
                                            {
                                                required: !directionSwitch,
                                                message: (
                                                    <span className="field-validation-message">
                                                    Please select action
                                                </span>
                                                ),
                                            },
                                        ]}
                                    >
                                        <Select
                                            labelInValue
                                            style={{ width: 160 }}
                                            placeholder="Select Action"
                                            className="drug-format-select rounded"
                                            options={directionActions}
                                            virtual={false} // Force all items to be displayed
                                            getPopupContainer={(trigger) => trigger.parentNode}
                                        />
                                    </Form.Item>
                                    <Form.Item<FieldType>
                                        label={
                                            <label className="field-title">
                                                Quantity
                                            </label>
                                        }
                                        className="ms-4"
                                        name="quantity"
                                        rules={[
                                            {
                                                required: !directionSwitch,
                                                message: (
                                                    <span className="field-validation-message">
                                                    Please enter quantity
                                                </span>
                                                ),
                                            },
                                            {
                                                validator: (_, value) => {
                                                    if (!value) {
                                                        return Promise.reject();
                                                    }

                                                    const numericValue =
                                                        parseFloat(value);
                                                    if (isNaN(numericValue)) {
                                                        return Promise.reject(
                                                            'Only numbers are allowed!',
                                                        );
                                                    }
                                                    if (numericValue < 0.0001) {
                                                        return Promise.reject(
                                                            'Min value is 0.0001',
                                                        );
                                                    }
                                                    return Promise.resolve();
                                                },
                                            },
                                        ]}
                                    >
                                        <Input
                                            placeholder="Input Dosage"
                                            maxLength={10}
                                            className="field-bg rounded custom-input-width"

                                        />
                                    </Form.Item>
                                    <Form.Item<FieldType>
                                        label={
                                            <label className="field-title">
                                                Form
                                            </label>
                                        }
                                        className="ms-4"
                                        name="formValue"
                                        rules={[
                                            {
                                                required: !directionSwitch,
                                                message: (
                                                    <span className="field-validation-message">
                                                    Please select form
                                                </span>
                                                ),
                                            },
                                        ]}
                                    >
                                        <Select
                                            className="drug-format-select rounded"
                                            placeholder="Select Form"
                                            style={{ width: 140 }}
                                            optionLabelProp="label"
                                            labelInValue
                                            options={directionForms}
                                            virtual={false} // Force all items to be displayed
                                            getPopupContainer={(trigger) => trigger.parentNode}
                                        />
                                    </Form.Item>
                                    <Form.Item<FieldType>
                                        label={
                                            <label className="field-title">
                                                Route
                                            </label>
                                        }
                                        className="ms-4"
                                        name="route"
                                        rules={[
                                            {
                                                required: !directionSwitch,
                                                message: (
                                                    <span className="field-validation-message">
                                                    Please select route
                                                </span>
                                                ),
                                            },
                                        ]}
                                    >
                                        <Select
                                            className="drug-format-select rounded"
                                            placeholder="Select Route"
                                            style={{ width: 145 }}
                                            options={directionRoute}
                                            optionLabelProp="label"
                                            labelInValue
                                            virtual={false} // Force all items to be displayed
                                            getPopupContainer={(trigger) => trigger.parentNode}
                                        />
                                    </Form.Item>
                                    <Form.Item<FieldType>
                                        label={
                                            <label className="field-title">
                                                Frequency
                                            </label>
                                        }
                                        className="ms-4"
                                        name="frequency"
                                        rules={[
                                            {
                                                required: !directionSwitch,
                                                message: 'Please select frequency',
                                            },
                                        ]}
                                    >
                                        <Select
                                            className="drug-format-select rounded"
                                            placeholder="Select Frequency"
                                            style={{ width: 240 }}
                                            optionLabelProp="label"
                                            labelInValue
                                            options={directionFrequency}
                                            onChange={handleChange}
                                            virtual={false} // Force all items to be displayed
                                            getPopupContainer={(trigger) => trigger.parentNode}
                                        />
                                    </Form.Item>
                                </div>
                            )}

                            <Form.Item label={null}>
                                <div className="mx-4">
                                    {cancelButton}
                                    <Button
                                        loading={isSubmitting}
                                        type="primary"
                                        className='form-button-width bg-light-blue'
                                        shape="round"
                                        onClick={handleSubmit}
                                    >
                                        {submitLabel}
                                    </Button>
                                </div>
                            </Form.Item>
                        </Form>
                        <Modal
                            width={1000} // Set custom width
                            bodyStyle={{ maxHeight: '100%', padding: '0' }}
                            confirmLoading={isLoadingDrugInteraction}
                            open={open}
                            closable={false}
                            onCancel={() => setOpen(false)}
                            footer={[
                                <Button
                                    key="cancel"
                                    onClick={() => setOpen(false)}
                                    className="bg-gray-200 hover:bg-gray-300 rounded"
                                >
                                    Close
                                </Button>,
                            ]}
                        >
                            {drugInteraction?.interactions?.map(
                                (interaction, index) => (
                                    <div>
                                        <div
                                            dangerouslySetInnerHTML={{
                                                __html: interaction.html,
                                            }}
                                        />
                                    </div>
                                ),
                            )}
                        </Modal>
                    </div>
                </Card>
            </Spin>
            <ConfirmationModal
                isOpen={isModalOpen}
                onClose={() => setIsModalOpen(false)}
                title="Successful!"
                message="The medication(s) were successfully added to the Medication Queue."
                primaryButtonText="View Medication Queue"
                secondaryButtonText="Add New Medication"
                onPrimaryButtonLinkToClick={()=> navigate(-1)}
                onSecondaryButtonLinkClick={()=> {
                    form.resetFields();
                    setDirectionSwitch(false);
                    setIsModalOpen(false);
                }}
            />
        </>

    );
};

export default OrderMedicationForm;
